import React, { useState } from "react";
import {
  Box,
  TextField,
  FormHelperText,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

/**
 * Password input styled like CreateDocumentModal standard TextFields
 * (white fill, #e5e7f6 border, #304fff focus).
 */
export default function PasswordField({
  value,
  onChange,
  label = "enterPassword",
  direction = "rtl",
  error = false,
  helperText = "",
  showPassword: showPasswordProp,
  handleClickShowPassword: handleClickShowPasswordProp,
  handleMouseDownPassword: handleMouseDownPasswordProp,
}) {
  const [internalShowPassword, setInternalShowPassword] = useState(false);
  const isControlled = typeof showPasswordProp === "boolean";
  const showPassword = isControlled ? showPasswordProp : internalShowPassword;

  const handleClickShowPassword =
    handleClickShowPasswordProp ||
    (() => setInternalShowPassword((prev) => !prev));

  const handleMouseDownPassword =
    handleMouseDownPasswordProp ||
    ((event) => event.preventDefault());

  const isRtl = direction === "rtl";

  const inputPropsStyle = {
    disableUnderline: true,
    className: `custom-input-box-sizing-toggle ${isRtl ? "rtl-input" : "ltr-input"}`,
    sx: {
      height: "48px",
      padding: "0 8px",
      borderRadius: "8px",
      background: "#ffffff",
      border: error ? "2px solid #c62828" : "2px solid #e5e7f6",
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        borderColor: error ? "#c62828" : "#304fff",
      },
      "&:focus-within": {
        border: `2px solid ${error ? "#c62828" : "#304fff"}`,
        boxShadow: error
          ? "0 0 0 2px rgba(198, 40, 40, 0.1)"
          : "0 0 0 2px rgba(48, 79, 255, 0.1)",
      },
      "& *": {
        boxSizing: "border-box !important",
      },
    },
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          aria-label={showPassword ? "hide the password" : "display the password"}
          onClick={handleClickShowPassword}
          onMouseDown={handleMouseDownPassword}
          edge="end"
          size="small"
          sx={{ color: "#304FFF" }}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%", mb: 2 }}>
      {label && (
        <Typography
          component="div"
          sx={{
            mb: 0.75,
            fontWeight: 600,
            fontSize: "0.875rem",
            textAlign: isRtl ? "left" : "right",
            color: error ? "#c62828" : "inherit",
          }}
        >
          {label}
        </Typography>
      )}
      <TextField
        fullWidth
        type={showPassword ? "text" : "password"}
        value={value ?? ""}
        onChange={onChange}
        variant="standard"
        autoComplete="new-password"
        error={error}
        placeholder={typeof label === "string" ? label : undefined}
        InputProps={inputPropsStyle}
        inputProps={{
          style: { textAlign: isRtl ? "right" : "left" },
        }}
      />
      {helperText ? (
        <FormHelperText error={error} sx={{ mx: 0, mt: 0.5, textAlign: isRtl ? "right" : "left" }}>
          {helperText}
        </FormHelperText>
      ) : null}
    </Box>
  );
}
